import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mcpTokensApi } from './mcp-tokens-api';

export const MCP_TOKENS_QUERY_KEY = ['mcp-tokens'] as const;

export function useMcpTokens() {
  return useQuery({ queryKey: MCP_TOKENS_QUERY_KEY, queryFn: mcpTokensApi.list });
}

/**
 * Creates a token. The list is refetched whatever the outcome (a 409 means the
 * list the screen holds is stale), and the mutation settles only once it is
 * back, so the new row is there when the reveal dialog opens.
 *
 * The answer carries the plaintext value, which must live only while the reveal
 * dialog shows it: `gcTime: 0` drops the mutation from the cache as soon as
 * nothing observes it, and the caller `reset()`s it once the value is handed over.
 */
export function useCreateMcpToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mcpTokensApi.create,
    gcTime: 0,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MCP_TOKENS_QUERY_KEY }),
    onError: () => queryClient.invalidateQueries({ queryKey: MCP_TOKENS_QUERY_KEY }),
  });
}

/**
 * Revokes a token; settles once the list shows it as « Révoqué ». The list is
 * refetched on failure too: the token may have been revoked elsewhere, and the
 * caller reads the fresh list to tell.
 */
export function useRevokeMcpToken() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mcpTokensApi.revoke,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: MCP_TOKENS_QUERY_KEY }),
    onError: () => queryClient.invalidateQueries({ queryKey: MCP_TOKENS_QUERY_KEY }),
  });
}
